import { useCallback, useEffect, useMemo, useState } from 'react';
import { markChatAsSeen, markChatAsUnread } from '../services/patient';
import type { ToastType } from '../providers/ToastProvider';

export function useMultiSelect(
    displayedItems: { patientId: string }[],
    unreadPatientIds: Set<string>,
    refreshMetadata: () => Promise<void>,
    showToast: (msg: string, type: ToastType) => void,
    resetDeps: unknown[]
) {
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);

    const exitSelectMode = useCallback(() => {
        setSelectMode(false);
        setSelectedIds(new Set());
    }, []);

    const toggleSelect = useCallback(
        (e: React.MouseEvent | React.ChangeEvent, patientId: string) => {
            e.stopPropagation();
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(patientId)) next.delete(patientId);
                else next.add(patientId);
                return next;
            });
        },
        []
    );

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const allDisplayed = displayedItems.map((r) => r.patientId);
            const allSelected = allDisplayed.every((id) => prev.has(id));
            if (allSelected) return new Set();
            return new Set(allDisplayed);
        });
    }, [displayedItems]);

    useEffect(() => {
        exitSelectMode();
    }, resetDeps);

    const selectionType = useMemo(() => {
        if (selectedIds.size === 0) return 'empty' as const;
        let hasUnread = false;
        let hasRead = false;
        for (const id of selectedIds) {
            if (unreadPatientIds.has(id)) hasUnread = true;
            else hasRead = true;
            if (hasUnread && hasRead) return 'mixed' as const;
        }
        return hasUnread ? ('unread' as const) : ('read' as const);
    }, [selectedIds, unreadPatientIds]);

    const handleBulkMarkAsDone = useCallback(async () => {
        if (selectedIds.size === 0) return;
        setIsBulkProcessing(true);
        const results = await Promise.allSettled(
            Array.from(selectedIds).map((id) => markChatAsSeen(id))
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        await refreshMetadata();
        exitSelectMode();
        if (failed > 0)
            showToast(`${failed} conversation${failed === 1 ? '' : 's'} failed to update`, 'error');
        setIsBulkProcessing(false);
    }, [selectedIds, refreshMetadata, showToast, exitSelectMode]);

    const handleBulkMarkAsUnread = useCallback(async () => {
        if (selectedIds.size === 0) return;
        setIsBulkProcessing(true);
        const results = await Promise.allSettled(
            Array.from(selectedIds).map((id) => markChatAsUnread(id))
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        await refreshMetadata();
        exitSelectMode();
        if (failed > 0)
            showToast(`${failed} conversation${failed === 1 ? '' : 's'} failed to update`, 'error');
        setIsBulkProcessing(false);
    }, [selectedIds, refreshMetadata, showToast, exitSelectMode]);

    return {
        selectMode,
        setSelectMode,
        selectedIds,
        setSelectedIds,
        isBulkProcessing,
        exitSelectMode,
        toggleSelect,
        toggleSelectAll,
        selectionType,
        handleBulkMarkAsDone,
        handleBulkMarkAsUnread
    };
}
